import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, ActionBar } from "@/components/PageBits";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Filter } from "lucide-react";

export default function Billing() {
  const [filters, setFilters] = useState({
    mitarbeiter: "ALLE", fahrzeug: "ALLE", team: "", datum_von: "", datum_bis: "",
  });
  const [users, setUsers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [data, setData] = useState({ breakdown: [], total: 0 });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data));
    api.get("/vehicles").then((r) => setVehicles(r.data));
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const p = {};
      Object.entries(filters).forEach(([k, v]) => { if (v && v !== "ALLE") p[k] = v; });
      const { data } = await api.get("/billing", { params: p });
      setData(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const exportCsv = async () => {
    const p = {};
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== "ALLE") p[k] = v; });
    const r = await api.get("/export/billing.csv", { params: p, responseType: "blob" });
    const url = window.URL.createObjectURL(r.data);
    const a = document.createElement("a"); a.href = url; a.download = "mitarbeiterabrechnung.csv"; a.click();
  };

  return (
    <div data-testid="billing-page">
      <PageHeader title="Mitarbeiterabrechnung" subtitle="Verdienst pro Mitarbeiter (anteilig je Auftrag)" />

      <div className="bg-white border rounded-md p-5 mb-4 grid grid-cols-2 md:grid-cols-5 gap-3 items-end" style={{ borderColor: "#E2E8F0" }}>
        <div>
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Mitarbeiter</label>
          <Select value={filters.mitarbeiter} onValueChange={(v) => setFilters((f) => ({ ...f, mitarbeiter: v }))}>
            <SelectTrigger className="h-12 mt-1.5" data-testid="billing-mitarbeiter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALLE">Alle</SelectItem>
              {users.map((u) => (<SelectItem key={u.id} value={u.name}>{u.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Fahrzeug</label>
          <Select value={filters.fahrzeug} onValueChange={(v) => setFilters((f) => ({ ...f, fahrzeug: v }))}>
            <SelectTrigger className="h-12 mt-1.5" data-testid="billing-fahrzeug"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALLE">Alle</SelectItem>
              {vehicles.map((v) => (<SelectItem key={v.id} value={v.id}>{v.kennzeichen}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Team</label>
          <Input className="h-12 mt-1.5" value={filters.team} onChange={(e) => setFilters((f) => ({ ...f, team: e.target.value }))} data-testid="billing-team" />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Von</label>
          <Input type="date" className="h-12 mt-1.5" value={filters.datum_von} onChange={(e) => setFilters((f) => ({ ...f, datum_von: e.target.value }))} data-testid="billing-von" />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Bis</label>
          <Input type="date" className="h-12 mt-1.5" value={filters.datum_bis} onChange={(e) => setFilters((f) => ({ ...f, datum_bis: e.target.value }))} data-testid="billing-bis" />
        </div>
        <Button onClick={load} className="h-12 bg-[#0A2540] hover:bg-[#113A63] text-white col-span-2 md:col-span-1" data-testid="billing-filter-btn">
          <Filter className="h-4 w-4 mr-2" /> Anwenden
        </Button>
      </div>

      <div className="bg-white border rounded-md mb-4" style={{ borderColor: "#E2E8F0" }}>
        <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: "#E2E8F0" }}>
          <h3 className="font-bold">Aufschlüsselung</h3>
          <div className="text-right">
            <div className="text-xs text-slate-500 uppercase font-semibold tracking-wider">Gesamtsumme</div>
            <div className="text-2xl font-bold tabular-nums" data-testid="billing-total">€ {(data.total || 0).toLocaleString("de-DE", { minimumFractionDigits: 2 })}</div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Mitarbeiter</th>
                <th className="text-right px-5 py-3 font-semibold">Aufträge</th>
                <th className="text-right px-5 py-3 font-semibold">Verdienst</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={3} className="py-10 text-center text-slate-400">Lädt…</td></tr>}
              {!loading && data.breakdown.length === 0 && <tr><td colSpan={3} className="py-10 text-center text-slate-400">Keine Daten</td></tr>}
              {data.breakdown.map((b) => (
                <tr key={b.mitarbeiter} className="border-t" style={{ borderColor: "#E2E8F0" }}>
                  <td className="px-5 py-3 font-semibold">{b.mitarbeiter}</td>
                  <td className="px-5 py-3 text-right tabular-nums">{b.anzahl}</td>
                  <td className="px-5 py-3 text-right tabular-nums font-bold">€ {b.summe.toLocaleString("de-DE", { minimumFractionDigits: 2 })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ActionBar onExport={exportCsv} onPrint={() => window.print()} hideNext />
    </div>
  );
}
